import { translateBatchWithDeepL } from './deepl';
import { getDeeplCode } from './languages';
import { en } from './en';

// Cache für übersetzte Sprachen
const translationCache: Map<string, any> = new Map();

// Basis-Übersetzung (Englisch als Fallback)
const BASE_TRANSLATIONS = en;

/**
 * Lädt Übersetzungen für eine Sprache
 * Verwendet Cache, localStorage und erstellt fehlende Übersetzungen via DeepL
 */
export async function loadTranslations(locale: string): Promise<any> {
    // 1. Prüfe In-Memory Cache
    if (translationCache.has(locale)) {
        console.log(`[i18n] Loaded ${locale} from memory cache`);
        return translationCache.get(locale);
    }

    // 2. Prüfe localStorage
    const stored = localStorage.getItem(`translations_${locale}`);
    if (stored) {
        try {
            const parsed = JSON.parse(stored);
            translationCache.set(locale, parsed);
            console.log(`[i18n] Loaded ${locale} from localStorage`);

            // Prüfe im Hintergrund auf fehlende Keys
            checkAndTranslateMissing(locale, parsed);

            return parsed;
        } catch (error) {
            console.error(`[i18n] Failed to parse stored translations for ${locale}`, error);
        }
    }

    // 3. Erstelle neue Übersetzungen via DeepL
    console.log(`[i18n] Creating translations for ${locale}...`);
    const translations = await translateAll(BASE_TRANSLATIONS, locale);

    // Speichere in Cache und localStorage
    translationCache.set(locale, translations);
    saveToLocalStorage(locale, translations);

    return translations;
}

/**
 * Übersetzt alle Keys einer Sprache
 */
async function translateAll(source: any, targetLocale: string): Promise<any> {
    const deeplCode = getDeeplCode(targetLocale);

    // Sammle alle zu übersetzenden Strings
    const stringsToTranslate: Array<{ path: string[]; value: string }> = [];

    function collectStrings(obj: any, path: string[] = []) {
        for (const key in obj) {
            const currentPath = [...path, key];

            if (typeof obj[key] === 'string') {
                stringsToTranslate.push({ path: currentPath, value: obj[key] });
            } else if (typeof obj[key] === 'object' && obj[key] !== null) {
                collectStrings(obj[key], currentPath);
            }
        }
    }

    collectStrings(source);

    console.log(`[i18n] Translating ${stringsToTranslate.length} strings to ${targetLocale}...`);

    // Übersetze in Batches (max 25 pro Request für Stabilität)
    const batchSize = 25;
    const translatedStrings: string[] = [];

    for (let i = 0; i < stringsToTranslate.length; i += batchSize) {
        const batch = stringsToTranslate.slice(i, i + batchSize);
        const texts = batch.map(item => item.value);

        try {
            const translations = await translateBatchWithDeepL(texts, deeplCode, 'EN');
            translatedStrings.push(...translations);

            console.log(`[i18n] Translated batch ${Math.floor(i / batchSize) + 1}/${Math.ceil(stringsToTranslate.length / batchSize)}`);

            // Kleine Verzögerung zwischen Batches um Rate-Limits zu vermeiden
            if (i + batchSize < stringsToTranslate.length) {
                await new Promise(resolve => setTimeout(resolve, 500));
            }
        } catch (error) {
            console.error(`[i18n] Translation batch failed`, error);
            // Verwende Original-Texte als Fallback
            translatedStrings.push(...texts);
        }
    }

    // Baue Übersetzungsobjekt wieder zusammen
    const result: any = {};

    stringsToTranslate.forEach((item, index) => {
        let current = result;

        for (let i = 0; i < item.path.length - 1; i++) {
            const key = item.path[i];
            if (!current[key]) {
                current[key] = {};
            }
            current = current[key];
        }

        const lastKey = item.path[item.path.length - 1];
        current[lastKey] = translatedStrings[index];
    });

    return result;
}

/**
 * Prüft auf fehlende Keys und übersetzt diese
 */
async function checkAndTranslateMissing(locale: string, existing: any): Promise<void> {
    const deeplCode = getDeeplCode(locale);
    const missingKeys: Array<{ path: string[]; value: string }> = [];

    function findMissing(source: any, target: any, path: string[] = []) {
        for (const key in source) {
            const currentPath = [...path, key];

            if (typeof source[key] === 'string') {
                if (!target[key]) {
                    missingKeys.push({ path: currentPath, value: source[key] });
                }
            } else if (typeof source[key] === 'object' && source[key] !== null) {
                if (!target[key] || typeof target[key] !== 'object') {
                    target[key] = {};
                }
                findMissing(source[key], target[key], currentPath);
            }
        }
    }

    findMissing(BASE_TRANSLATIONS, existing);

    if (missingKeys.length > 0) {
        console.log(`[i18n] Found ${missingKeys.length} missing keys for ${locale}, translating...`);

        const texts = missingKeys.map(item => item.value);
        const translations = await translateBatchWithDeepL(texts, deeplCode, 'EN');

        // Füge fehlende Übersetzungen hinzu
        missingKeys.forEach((item, index) => {
            let current = existing;

            for (let i = 0; i < item.path.length - 1; i++) {
                current = current[item.path[i]];
            }

            const lastKey = item.path[item.path.length - 1];
            current[lastKey] = translations[index];
        });

        // Aktualisiere Cache und localStorage
        translationCache.set(locale, existing);
        saveToLocalStorage(locale, existing);

        console.log(`[i18n] Updated ${locale} with ${missingKeys.length} new translations`);
    }
}

/**
 * Speichert Übersetzungen in localStorage
 */
function saveToLocalStorage(locale: string, translations: any): void {
    try {
        localStorage.setItem(`translations_${locale}`, JSON.stringify(translations));
        localStorage.setItem(`translations_${locale}_timestamp`, Date.now().toString());
    } catch (error) {
        console.error(`[i18n] Failed to save translations to localStorage`, error);
    }
}

/**
 * Löscht Cache für eine Sprache (z.B. zum Neuladen erzwingen)
 */
export function clearTranslationCache(locale?: string): void {
    if (locale) {
        translationCache.delete(locale);
        localStorage.removeItem(`translations_${locale}`);
        localStorage.removeItem(`translations_${locale}_timestamp`);
        console.log(`[i18n] Cleared cache for ${locale}`);
    } else {
        translationCache.clear();
        Object.keys(localStorage).forEach(key => {
            if (key.startsWith('translations_')) {
                localStorage.removeItem(key);
            }
        });
        console.log(`[i18n] Cleared all translation caches`);
    }
}

/**
 * Exportiert Übersetzungen (z.B. zum Speichern auf dem Server)
 */
export function exportTranslations(locale: string): string | null {
    const translations = translationCache.get(locale);
    if (translations) {
        return JSON.stringify(translations, null, 2);
    }
    return null;
}

// Expose globally for debugging
if (typeof window !== 'undefined') {
    (window as any).clearTranslationCache = clearTranslationCache;
    console.log('[i18n] Debug: window.clearTranslationCache() available');
}
